import { useCallback, useSyncExternalStore } from "react";
import { resolveRoute, type RouteName } from "./routes";

type Listener = () => void;

const listeners = new Set<Listener>();

function emit(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  window.addEventListener("popstate", listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener("popstate", listener);
  };
}

function snapshot(): string {
  return window.location.pathname + window.location.search;
}

/** Điều hướng client-side. `replace` dùng khi không muốn thêm mục vào lịch sử. */
export function navigate(path: string, options: { replace?: boolean } = {}): void {
  const current = snapshot();
  if (current === path) return;
  if (options.replace) history.replaceState({}, "", path);
  else history.pushState({}, "", path);
  window.scrollTo(0, 0);
  emit();
}

export function useLocation(): { pathname: string; search: string; route: RouteName } {
  const href = useSyncExternalStore(subscribe, snapshot, () => "/");
  const [pathname = "/", search = ""] = href.split("?") as [string?, string?];
  return { pathname, search: search ? `?${search}` : "", route: resolveRoute(pathname) };
}

export function useQueryParam(name: string): string {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name) || "";
}

/**
 * Handler cho <a href> nội bộ: chặn reload cả trang nhưng vẫn để người dùng
 * mở tab mới bằng ctrl/cmd-click hoặc chuột giữa.
 */
export function useFollow(): (event: {
  preventDefault: () => void;
  detail?: { href?: string; external?: boolean };
}) => void {
  return useCallback((event) => {
    const href = event.detail?.href;
    if (!href || event.detail?.external) return;
    if (!href.startsWith("/")) return;
    event.preventDefault();
    navigate(href);
  }, []);
}
